import { useMarketNews } from '@/hooks/useMarketNews';
import type { NewsArticle } from '@/hooks/useMarketNews';
import './MarketNews.css';

function timeAgo(iso: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function sentimentLabel(article: NewsArticle): { text: string; className: string } | null {
  if (!article.sentiment) return null;
  const bull = article.sentiment.bullishPercent ?? 0;
  const bear = article.sentiment.bearishPercent ?? 0;
  if (bull > 60) return { text: 'Bullish', className: 'market-news__sentiment--bullish' };
  if (bear > 60) return { text: 'Bearish', className: 'market-news__sentiment--bearish' };
  if (bull > 0 || bear > 0) return { text: 'Mixed', className: 'market-news__sentiment--mixed' };
  return null;
}

interface MarketNewsProps {
  limit?: number;
}

export default function MarketNews({ limit = 15 }: MarketNewsProps) {
  const { articles, loading, error } = useMarketNews(limit, 300_000);

  return (
    <div className="market-news">
      {loading && articles.length === 0 && (
        <div className="market-news__loading">Loading news...</div>
      )}
      {error && articles.length === 0 && (
        <div className="market-news__error">News feed unavailable</div>
      )}
      {articles.map((article) => {
        const sentiment = sentimentLabel(article);
        return (
          <a
            key={article.id}
            className="market-news__item"
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            title={article.summary}
          >
            <div className="market-news__item-header">
              <span className="market-news__source">{article.source}</span>
              <span className="market-news__time">{timeAgo(article.publishedAt)}</span>
            </div>
            <div className="market-news__title">{article.title}</div>
            <div className="market-news__meta">
              {sentiment && (
                <span className={`market-news__sentiment ${sentiment.className}`}>
                  {sentiment.text}
                </span>
              )}
              {article.tickers.length > 0 && (
                <span className="market-news__tickers">
                  {article.tickers.slice(0, 4).map((t) => (
                    <span key={t} className="market-news__ticker">{t}</span>
                  ))}
                </span>
              )}
            </div>
          </a>
        );
      })}
    </div>
  );
}
